"use client";

/**
 * PlatformRunDiagram — the signature animated visual for /platform.
 *
 * A 5-stage horizontal pipeline (Route → Plan → Run → Cite → Verify)
 * connected by SVG paths. On scroll-in, a brand-red pulse travels
 * through the pipeline node-by-node, leaving each node in a "done"
 * state behind it. The Run stage briefly branches downward to two
 * "satellite" agents to show that work hands off mid-pipeline.
 *
 * Visual idiom borrowed from product-mocks/workflow-mock.tsx but
 * tuned for the platform page — bigger, brighter, no window chrome.
 * Pure presentation; the real router lives in shared_backend/.
 */
import { useEffect, useRef, useState } from "react";
import {
  Compass,
  ListTree,
  PlayCircle,
  Quote,
  CheckCircle2,
  Search,
  PenLine,
  Globe,
} from "lucide-react";

type Stage = {
  id: string;
  title: string;
  detail: string;
  icon: typeof Compass;
  x: number; // % across canvas
  y: number; // % down
};

const STAGES: Stage[] = [
  { id: "route",  title: "Route",  detail: "agent · selected",  icon: Compass,     x: 8,  y: 32 },
  { id: "plan",   title: "Plan",   detail: "04 steps · drafted", icon: ListTree,    x: 28, y: 32 },
  { id: "run",    title: "Run",    detail: "tools · live",       icon: PlayCircle,  x: 50, y: 32 },
  { id: "cite",   title: "Cite",   detail: "23 sources · linked",icon: Quote,       x: 72, y: 32 },
  { id: "verify", title: "Verify", detail: "claims · cross-read",icon: CheckCircle2,x: 92, y: 32 },
];

type Satellite = {
  id: string;
  title: string;
  detail: string;
  icon: typeof Search;
  x: number;
  y: number;
};

// Satellite agents that the Run stage temporarily hands work to.
const SATELLITES: Satellite[] = [
  { id: "researcher", title: "Researcher", detail: "fetches · 11 sources",  icon: Search, x: 42, y: 78 },
  { id: "writer",     title: "Writer",     detail: "drafts · 1 paragraph",  icon: PenLine, x: 58, y: 78 },
];

function linePath(ax: number, ay: number, bx: number, by: number) {
  // Curved Bezier between two points; gentle midpoint sag.
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2 + (ay === by ? 0 : 6);
  return `M ${ax} ${ay} Q ${mx} ${my} ${bx} ${by}`;
}

export function PlatformRunDiagram() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [doneSet, setDoneSet] = useState<Set<number>>(new Set());
  const [satOpen, setSatOpen] = useState(false);
  const armed = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setActiveIdx(-1);
      setDoneSet(new Set(STAGES.map((_, i) => i)));
      setSatOpen(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;

        STAGES.forEach((_, i) => {
          setTimeout(() => setActiveIdx(i), 500 + i * 1000);
          setTimeout(() => {
            setDoneSet((prev) => {
              const n = new Set(prev);
              n.add(i);
              return n;
            });
          }, 500 + i * 1000 + 700);
          if (STAGES[i].id === "run") {
            setTimeout(() => setSatOpen(true), 500 + i * 1000 + 200);
            setTimeout(() => setSatOpen(false), 500 + i * 1000 + 1300);
          }
        });
        setTimeout(() => setActiveIdx(-1), 500 + STAGES.length * 1000 + 200);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full">
      {/* Faint paper-warm glow behind the canvas */}
      <div
        aria-hidden
        className="absolute -inset-x-8 -inset-y-8 rounded-[36px] bg-foreground/[0.015] blur-2xl"
      />

      {/* On phones the 5-node horizontal pipeline can't fit side-by-side
          (the fixed-width nodes would overlap), so the whole board scrolls
          horizontally below its natural width instead of collapsing. */}
      <div className="relative overflow-x-auto">
      <div className="relative rounded-2xl border hairline-strong bg-card/70 overflow-hidden min-w-[560px]">
        {/* Top status strip */}
        <div className="flex items-center gap-2 px-4 h-9 border-b hairline">
          <span className="h-2 w-2 rounded-full bg-foreground/15" />
          <span className="h-2 w-2 rounded-full bg-foreground/15" />
          <span className="h-2 w-2 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[10px] tracking-[0.2em] uppercase font-mono text-foreground/65">
            agent run · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (activeIdx >= 0
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : "bg-foreground/35")
              }
            />
            {activeIdx >= 0 ? "Running" : "Idle"}
          </span>
        </div>

        {/* Canvas */}
        <div className="relative w-full h-[360px] md:h-[420px] bg-background">
          {/* Dotted background */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.55]"
            style={{
              backgroundImage:
                "radial-gradient(currentColor 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              color: "hsl(var(--foreground) / 0.06)",
            }}
          />

          {/* Connector lines (main pipeline + satellite branches) */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            {/* Main pipeline */}
            {STAGES.slice(0, -1).map((s, i) => {
              const next = STAGES[i + 1];
              const pulseHere = activeIdx === i + 1;
              const isDone = doneSet.has(i + 1);
              return (
                <g key={`m-${i}`}>
                  <path
                    d={linePath(s.x, s.y, next.x, next.y)}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity={isDone ? 0.45 : 0.2}
                    strokeWidth="0.32"
                    vectorEffect="non-scaling-stroke"
                  />
                  {pulseHere && (
                    <path
                      d={linePath(s.x, s.y, next.x, next.y)}
                      fill="none"
                      stroke="hsl(var(--accent))"
                      strokeOpacity="0.95"
                      strokeWidth="0.6"
                      strokeDasharray="3 4"
                      vectorEffect="non-scaling-stroke"
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        from="14"
                        to="0"
                        dur="0.7s"
                        repeatCount="indefinite"
                      />
                    </path>
                  )}
                </g>
              );
            })}

            {/* Satellite branches from Run stage (index 2) — dashed always */}
            {SATELLITES.map((sat) => {
              const from = STAGES[2];
              return (
                <g key={`sat-${sat.id}`}>
                  <path
                    d={linePath(from.x, from.y, sat.x, sat.y)}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity={satOpen ? 0.4 : 0.12}
                    strokeWidth="0.28"
                    strokeDasharray="2 3"
                    vectorEffect="non-scaling-stroke"
                  />
                  {satOpen && (
                    <path
                      d={linePath(from.x, from.y, sat.x, sat.y)}
                      fill="none"
                      stroke="hsl(var(--accent))"
                      strokeOpacity="0.75"
                      strokeWidth="0.55"
                      strokeDasharray="1.6 3"
                      vectorEffect="non-scaling-stroke"
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        from="9"
                        to="0"
                        dur="0.55s"
                        repeatCount="indefinite"
                      />
                    </path>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Pipeline nodes */}
          <div className="absolute inset-0">
            {STAGES.map((s, i) => {
              const Icon = s.icon;
              const isActive = activeIdx === i;
              const isDone = doneSet.has(i);
              return (
                <div
                  key={s.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${s.x}%`, top: `${s.y}%` }}
                >
                  <div
                    className={
                      "relative rounded-xl border bg-card px-3 py-2.5 min-w-[120px] md:min-w-[140px] transition shadow-[0_2px_8px_-4px_hsl(var(--foreground)/0.18)] " +
                      (isActive
                        ? "hairline-strong border-[hsl(var(--accent))]/45 ring-2 ring-[hsl(var(--accent))]/15"
                        : isDone
                        ? "hairline-strong"
                        : "hairline")
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          "inline-flex h-7 w-7 items-center justify-center rounded-md " +
                          (isActive
                            ? "bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))]"
                            : isDone
                            ? "bg-foreground/[0.06] text-foreground/85"
                            : "bg-foreground/[0.04] text-foreground/65")
                        }
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium leading-tight">
                          {String(i + 1).padStart(2, "0")} · {s.title}
                        </div>
                        <div className="text-[10px] font-mono text-foreground/65 leading-tight truncate">
                          {s.detail}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Satellite nodes */}
            {SATELLITES.map((sat) => {
              const Icon = sat.icon;
              return (
                <div
                  key={sat.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2 transition-opacity duration-500"
                  style={{
                    left: `${sat.x}%`,
                    top: `${sat.y}%`,
                    opacity: satOpen ? 1 : 0.32,
                  }}
                >
                  <div
                    className={
                      "rounded-xl border bg-card/85 px-3 py-2 min-w-[120px] " +
                      (satOpen ? "hairline-strong" : "hairline")
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          "inline-flex h-6 w-6 items-center justify-center rounded-md " +
                          (satOpen
                            ? "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]"
                            : "bg-foreground/[0.04] text-foreground/65")
                        }
                      >
                        <Icon className="h-3 w-3" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[11px] font-medium leading-tight">
                          {sat.title}
                        </div>
                        <div className="text-[9px] font-mono text-foreground/65 leading-tight">
                          {sat.detail}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Floating annotations */}
            <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 text-[9px] uppercase font-mono tracking-[0.16em] text-foreground/65">
              <Globe className="h-3 w-3" /> open web · your library · live tools
            </div>
            <div className="absolute right-3 bottom-3 inline-flex items-center gap-1.5 text-[9px] uppercase font-mono tracking-[0.16em] text-foreground/65">
              footnotes · re-read · result
            </div>
          </div>
        </div>

        {/* Footer ruler — arbitrary-value grid so it stays 5-across inside the
            scrollable card on mobile (bare grid-cols-5 gets collapsed to 1col
            by the shared mobile rule). */}
        <div className="grid grid-cols-[repeat(5,minmax(0,1fr))] border-t hairline">
          {STAGES.map((s, i) => (
            <div
              key={`f-${s.id}`}
              className={
                "px-3 py-2 text-[9.5px] font-mono uppercase tracking-[0.18em] " +
                (i < STAGES.length - 1 ? "border-r hairline " : "") +
                (doneSet.has(i)
                  ? "text-foreground/85"
                  : activeIdx === i
                  ? "text-[hsl(var(--accent))]"
                  : "text-foreground/65")
              }
            >
              {String(i + 1).padStart(2, "0")} · {s.title}
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
